import * as Sentry from '@sentry/react'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { Navigate, useLocation } from 'react-router'
import { ApiError } from '../../utils/api-client/api-error'
import { ZodParseError } from '../../utils/api-client/api-hooks'
import { ErrorDetail } from '../../utils/error-handling/error-detail'
import { isAuthError } from '../../utils/error-utils'
import { ErrorBoundaryFallback } from './error-boundary-fallback'

const AuthRedirect: React.FC = () => {
  const location = useLocation()
  // Preserve the query string so post-login lands on the exact deep link (tab, tool, drawer, etc.),
  // not just the pathname. URLSearchParams encodes the value symmetrically with the read sites.
  const redirectPath = `${location.pathname}${location.search}`
  const params = new URLSearchParams({ 'redirect-path': redirectPath })
  return <Navigate to={`/login?${params.toString()}`} replace />
}

type ErrorBoundaryProps = {
  children: React.ReactNode
  fallbackTitle?: string
  /** Rendered instead of the default retry fallback when the boundary catches an error. */
  fallback?: React.ReactNode
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallbackTitle = 'Failed to load data',
  fallback,
}) => (
  <QueryErrorResetBoundary>
    {({ reset }) => (
      <Sentry.ErrorBoundary
        onReset={reset}
        fallback={errorData => {
          const error = errorData.error instanceof Error ? errorData.error : new Error(String(errorData.error))

          if (isAuthError(error)) {
            return <AuthRedirect />
          }

          if (fallback !== undefined) {
            return <>{fallback}</>
          }

          const title =
            error instanceof ApiError && error.problemTitle
              ? error.problemTitle
              : error instanceof ZodParseError
                ? 'Unexpected issue'
                : fallbackTitle
          const detail = <ErrorDetail error={error} />

          return <ErrorBoundaryFallback title={title} detail={detail} onRetry={() => errorData.resetError()} />
        }}
      >
        {children}
      </Sentry.ErrorBoundary>
    )}
  </QueryErrorResetBoundary>
)
